import React, { useState, useEffect } from 'react'
import { Modal, message } from 'antd'
import PropTypes from 'prop-types'
import { epagAPI } from '../../../../API/epagAPI'
import { useGetToken } from '../../../../hooks/useGetToken'
import { useInputValue } from '../../../../hooks/useInputValue'
import { isEmailAddress } from '../../../../utils/utils'
import { Text } from '../../../../constants/TextConstants'
import { Input, TitleBox, EmptyDiv, ErrorText } from './styles'

export const ModalSupport = ({ open, setModal, isAdminTab }) => {
  const functionalState = { role: 'Functional', status: 'functional' }
  const supportState = { role: 'Support', status: 'support' }
  const onOkDisplay = isAdminTab ? 'initial' : 'none'
  const { token } = useGetToken()
  const inputFunctional = useInputValue('')
  const inputSupport = useInputValue('')
  const [functionalProfile, setFunctionalProfile] = useState(null)
  const [supportProfile, setSupportProfile] = useState(null)
  const [isFunctionalChanged, setIsFunctionalChanged] = useState(false)
  const [isSupportChanged, setIsSupportChanged] = useState(false)
  const [isInvalidAction, setIsInvalidAction] = useState(false)
  const [isRefresh, setIsRefresh] = useState(true)

  useEffect(() => {
    let mounted = true
    if (token && isRefresh) {
      epagAPI
        .GetSupportEmails(token)
        .then((profiles) => {
          if (mounted && profiles[0]) {
            setIsRefresh(false)
            setFunctionalProfile(profiles[0].support.functional_email)
            setSupportProfile(profiles[0].support.support_email)
            inputFunctional.setValue(profiles[0].support.functional_email)
            inputSupport.setValue(profiles[0].support.support_email)
            setIsInvalidAction(false)
          } else {
            inputFunctional.setValue(Text.DefaultSupportEmail)
            inputSupport.setValue(Text.DefaultSupportEmail)
            setIsInvalidAction(true)
          }
        })
        .catch((error) => {
          if (mounted) {
            console.error(error)
            setIsRefresh(false)
          }
        })
    }
    return () => {
      mounted = false
    }
  }, [token, isRefresh])

  const validateDifferencesBeforeSubmit = (functionalEmail, supportEmail, notify) => {
    const functionalHasDifferences = functionalProfile !== functionalEmail
    const supportHasDifferences = supportProfile !== supportEmail
    try {
      if (!functionalHasDifferences && !supportHasDifferences) {
        setModal(false)
        message.warning(Text.SupportEmailsHaveNoDifferences, 5)
      } else {
        if (functionalHasDifferences) {
          onSubmit(functionalEmail, functionalState.status, notify)
        }
        if (supportHasDifferences) {
          onSubmit(supportEmail, supportState.status, notify)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
  const onOk = () => {
    if (isAdminTab) {
      const functionalEmail = inputFunctional.value.trim()
      const supportEmail = inputSupport.value.trim()
      const isSupportEmailValid = isEmailAddress(supportEmail)
      const isFunctionalEmailValid = isEmailAddress(functionalEmail)
      if (!isFunctionalEmailValid) {
        inputFunctional.setError(true)
        inputFunctional.setErrorText(Text.InvalidEmail)
      }
      if (!isSupportEmailValid) {
        inputSupport.setError(true)
        inputSupport.setErrorText(Text.InvalidEmail)
      }
      if (isFunctionalEmailValid && isSupportEmailValid) {
        validateDifferencesBeforeSubmit(functionalEmail, supportEmail, true)
      }
    }
  }

  const onCancel = () => {
    inputFunctional.setError(false)
    if (isFunctionalChanged && !isEmailAddress(inputFunctional.value.trim())) {
      inputFunctional.setValue('')
    }
    inputSupport.setError(false)
    if (isSupportChanged && !isEmailAddress(inputSupport.value.trim())) {
      inputSupport.setValue('')
    }
    setIsRefresh(isFunctionalChanged || isSupportChanged)
    setModal(false)
  }

  const onSubmit = (email, type, notify) => {
    if (token) {
      epagAPI
        .UpdateSupportEmails(email, type, token)
        .then(() => {
          const successMessage = (type) => {
            if (type === functionalState.status) {
              setFunctionalProfile(email)
              return 'The Functional Email was saved'
            }
            if (type === supportState.status) {
              setSupportProfile(email)
              return 'The Support Email was saved'
            }
            return 'Email were updated'
          }
          setIsFunctionalChanged(false)
          setIsSupportChanged(false)
          if (notify) {
            setModal(false)
            message.success(successMessage(type), 4)
          }
        })
        .catch((error) => {
          console.error(error)
          if (notify) {
            message.error('An error occurred while saving', 4)
          }
        })
    }
  }

  const onChangeFunctional = (e) => {
    inputFunctional.onChange(e)
    setIsFunctionalChanged(true)
  }

  const onChangeSupport = (e) => {
    inputSupport.onChange(e)
    setIsSupportChanged(true)
  }

  return (
    <Modal
      data-testid='support-modal'
      okText={'Save'}
      cancelText={isAdminTab ? 'Cancel' : 'Close'}
      title={'Support'}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}
      okButtonProps={{ style: { display: onOkDisplay }, disabled: isInvalidAction }}>
      {isAdminTab ? (
        <section>
          <article style={{ marginTop: '1px' }}>
            <TitleBox style={{ marginLeft: '4px' }}>Functional Email</TitleBox>
            <Input
              value={inputFunctional.value}
              onChange={onChangeFunctional}
              disabled={!isAdminTab || isInvalidAction}
              placeholder='enter the email in charge'
              style={{ margin: '4px' }}
            />
            {inputFunctional.ErrorValue ? <ErrorText>{inputFunctional.ErrorText}</ErrorText> : <EmptyDiv />}
          </article>
          <article style={{ marginTop: '15px' }}>
            <TitleBox style={{ marginLeft: '4px' }}>Support Email</TitleBox>
            <Input
              value={inputSupport.value}
              onChange={onChangeSupport}
              disabled={!isAdminTab || isInvalidAction}
              placeholder='enter the email in charge'
              style={{ margin: '4px' }}
            />
            {inputSupport.ErrorValue ? <ErrorText>{inputSupport.ErrorText}</ErrorText> : <EmptyDiv />}
            {isInvalidAction ? <ErrorText>{Text.DefaultErrorMessageSupportEmail}</ErrorText> : <EmptyDiv />}
          </article>
        </section>
      ) : (
        <div>
          <p style={{ marginTop: '2px', marginBottom: '3px' }}>If you have doubts or questions, please email us to:</p>
          {inputFunctional.value ? (
            <a
              onClick={() => {
                window.location = `mailto:${inputFunctional.value}`
              }}>
              {inputFunctional.value}
            </a>
          ) : (
            <TitleBox>{Text.DefaultSupportEmail}</TitleBox>
          )}
          <p style={{ marginTop: '12px', marginBottom: '3px' }}>If you want to report an issue, please email us to:</p>
          {inputSupport.value ? (
            <a
              onClick={() => {
                window.location = `mailto:${inputSupport.value}`
              }}>
              {inputSupport.value}
            </a>
          ) : (
            <TitleBox>{Text.DefaultSupportEmail}</TitleBox>
          )}
        </div>
      )}
    </Modal>
  )
}

ModalSupport.propTypes = {
  open: PropTypes.bool,
  isAdminTab: PropTypes.bool,
  setModal: PropTypes.func
}
